import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
// Public Pages
import Home from './pages/public/home/Home';
import News from './pages/public/News';
import Leadership from './pages/public/Leadership';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import NewsDetail from './pages/public/NewsDetail';
import About from './pages/public/About';
import Programs from './pages/public/Programs';
import Privacy from './pages/public/Privacy';
import Terms from './pages/public/Terms';
//import Contact from './pages/public/Contact';
import Team from './pages/public/Team';
import ProgramDetail from './pages/public/ProgramDetails';
import ProgramRegistrationForm from './pages/admin/program/ProgramRegistrationForm';
import Profile from './pages/user/Profile';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import NewsLayout from './pages/admin/news/NewsLayout';
import MainLayout from './components/layout/MainLayout';

import NewsManagement from './pages/admin//news/NewsManagement';
import NewsDashboard from './pages/admin/dashboard/components/NewsDashboard';
import UserManagement from './pages/admin/users/UserManagement';
import ProgramsDashboard from './pages/admin/dashboard/program/ProgramsDashboard';
import UserDashboard from './pages/admin/dashboard/components/UserDashboard';
import LibraryDashboard from './pages/admin/dashboard/components/LibraryDashboard';
import UsersLayout from './pages/admin/users/UsersLayout';
import Reports from './pages/admin/Reports';
import AccountingManagement from './pages/admin/accounting/accountingManagemnent';
import AccountingLayout from './pages/admin/accounting/AccountingLayout';
import AccountingCategoriesManagement from './pages/admin/accounting/AccountingCategoriesManagement';
import AccountingVendorsManagement from './pages/admin/accounting/AccountingVendorsManagement';
import AccountingDashboard from './pages/admin/dashboard/accounting/AccountingDashboard';

// App.tsx - Add these routes


import ProgramList from './pages/admin/program/ProgramList';
import ProgramForm from './pages/admin/program/ProgramForm';
import PublishProgram from './pages/admin/program/PublishProgram';
import ProgramSessions from './pages/admin/program/ProgramSessions';
import ProgramRegistrations from './pages/admin/program/ProgramRegistrations';
import ProgramReport from './pages/admin/program/report/ProgramReport';
import AdminProgramRegistrationForm from './pages/admin/program/AdminProgramRegistrationForm';
import ProgramEvents from './pages/admin/program/ProgramEvents';


// Update your routing configuration
import LibraryLayout from './pages/admin/library/LibraryLayout';
import MemberManagement from './pages/admin/library/MemberManagement';
import CategoryManagement from './pages/admin/library/CategoryManagement';
import BookManagement from './pages/admin/library/BookManagement';
import AttendanceManagement from './pages/admin/library/AttendanceManagement';
import AddMemberModal from './pages/admin/library/AddMemberModal';
import ViewMemberPage from './pages/admin/library/ViewMemberPage';
import EditMemberPage from './pages/admin/library/EditMemberPage';
import TopAttendees from './pages/admin/library/TopAttendees';
// Super Admin Pages
import SuperAdminSettings from './pages/super-admin/Settings';
import ProgramLayout from './pages/admin/program/ProgramLayout';
import { initializeDashboardCache } from './firebase/dashboardCacheService';
import { initializeLibraryStats } from './firebase/libraryFirestoreService';
import { useAuth } from './contexts/AuthContext';
import ScrollToTop from './components/ScrollToTop';


  


function CacheInitializer() {
  const { loading, currentUser, userProfile, canViewSection } = useAuth();

  useEffect(() => {
    if (loading) return;

    const initCacheSystems = async () => {
      try {
        if (!currentUser || !userProfile) {
          console.log('Cache init skipped: no authenticated user');
          return;
        }

        const hasDashboardAccess =
          userProfile.role === 'admin' ||
          userProfile.role === 'super_admin' ||
          ['news', 'library', 'programs', 'accounting', 'users'].some(section => canViewSection(section));

        if (!hasDashboardAccess) {
          console.log('Cache init skipped: user has no dashboard access');
          return;
        }

        const sessionKey = `dashboard_cache_initialized_${currentUser.uid}`;
        if (typeof window !== 'undefined' && window.sessionStorage.getItem(sessionKey)) {
          return;
        }

        await initializeDashboardCache();
        await initializeLibraryStats();
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(sessionKey, '1');
        }
        console.log('✅ Cache systems initialized');
      } catch (error) {
        console.error('❌ Failed to initialize cache systems:', error);
      }
    };

    initCacheSystems();
  }, [loading, currentUser, userProfile, canViewSection]);

  return null;
}

function LegacyNewsRedirect() {
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const targetPath = id ? `/activities/${id}` : '/activities';

  return <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />;
}

function App() {

  return (
       <ErrorBoundary>
    <Router>
      <AuthProvider>
        <CacheInitializer />
        <ScrollToTop />
        <Routes>
          <Route element={<MainLayout />}> 
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/user/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/news" element={<LegacyNewsRedirect />} />
            <Route path="/news/:id" element={<LegacyNewsRedirect />} />
            <Route path="/activities" element={<News />} />
            <Route path="/activities/:id" element={<NewsDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/leadership" element={<Leadership />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/team" element={<Team />} />
            <Route path="/programs" element={<Programs />} />
            {/* <Route path="/contact" element={<Contact />} /> */}
             <Route path="/programs" element={<Programs />} />
            <Route path="/programs/:programId" element={<ProgramDetail />} />
            <Route path="/programs/:programId/register" element={<ProgramRegistrationForm />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="dashboard" replace />} />
        
            <Route path="news" element={
              <ProtectedRoute requiredRole="admin">
                {/* NewsLayout provides tab navigation and Outlet for child routes */}
                <React.Suspense fallback={null}>
                  <NewsLayout />
                </React.Suspense>
              </ProtectedRoute>
            }>
              <Route path="dashboard" element={<NewsDashboard />} />
              <Route index element={<NewsManagement />} />
              <Route path="about" element={<NewsManagement />} />
          
              <Route path="creators-hub" element={<NewsManagement />} />
            </Route>
            <Route path="users" element={<UsersLayout />}>
              <Route index element={<UserDashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="roles" element={<UserManagement />} />
              <Route path="invitations" element={<UserManagement />} />
            </Route>
            <Route path="accounting" element={<AccountingLayout />}>
              <Route index element={<AccountingDashboard />} />
              <Route path="categories" element={<AccountingCategoriesManagement />} />
              <Route path="vendors" element={<AccountingVendorsManagement />} />
              <Route path="management" element={<AccountingManagement />} />
            </Route>
            <Route path="reports" element={<Reports />} />
            
            {/* Library Management */}
              <Route path="library" element={<LibraryLayout />}>
                <Route index element={<LibraryDashboard />} />
                <Route path="members" element={<MemberManagement />} />
                <Route path="members/view/:id" element={<ViewMemberPage />} />
                <Route path="members/edit/:id" element={<EditMemberPage />} />
                <Route path="books" element={<BookManagement />} />
                <Route path="categories" element={<CategoryManagement />} />
                <Route path="attendance" element={<AttendanceManagement />} />
                <Route path="top-attendees" element={<TopAttendees />} />
                <Route path="add-member" element={<AddMemberModal isOpen={true} onClose={() => window.history.back()} onSave={() => {}} />} />
              </Route>


        {/* Program Management */}
          <Route path="programs" element={<ProgramLayout />}>
          <Route index element={<ProgramList />} />
          <Route path="dashboard" element={<ProgramsDashboard />} />
          <Route path="create" element={<ProgramForm />} />
          <Route path="edit/:id" element={<ProgramForm />} />
          <Route path="sessions" element={<ProgramSessions />} />
          <Route path="registrations" element={<ProgramRegistrations />} />
          <Route path="registrations/add" element={<AdminProgramRegistrationForm />} />
          <Route path="events" element={<ProgramEvents />} />
          <Route path="publish" element={<PublishProgram />} />
          <Route path="report" element={<ProgramReport />} />
        </Route>

            {/* Super Admin Only */}
            <Route path="settings" element={
              <ProtectedRoute requiredRole="super_admin">
                <SuperAdminSettings />
              </ProtectedRoute>
            } />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
